<template>
  <k-modal id="rejecting-otj-modal" :show="showModal" @close="$emit('close')">
    <template #header>
      <h3>Confirm Rejection</h3>
    </template>
    <template #body>
      <div v-if="otjData" class="otj-details-section">
        <h4>OTJ Details</h4>
        <div class="otj-detail-container">
          <div class="otj-detail-grid">
            <div class="otj-detail-item">
              <strong>
                Submitted:
              </strong>
              <span>{{ parseTimestamp(otjData.date_completed, undefined, 'd MMM yyyy') }}</span>
              <k-tooltip text="Date the OTJ entry was submitted by the learner">
                <i class="fas fa-info-circle"></i>
              </k-tooltip>
            </div>
            <div class="otj-detail-item">
              <strong>
                Activity Start:
              </strong>
              <span>{{ parseTimestamp(otjData.start_time, undefined, 'd MMM yyyy HH:mm') }}</span>
              <k-tooltip text="Date the activity took place">
                <i class="fas fa-info-circle"></i>
              </k-tooltip>
            </div>
            <div class="otj-detail-item">
              <strong>Time Spent:</strong>
              <span>{{ formatTimeSpent(otjData.time_spent) }}</span>
            </div>
            <div class="otj-detail-item">
              <strong>Learning Activity Type:</strong>
              <span>{{ otjData.category }}</span>
            </div>
          </div>

          <div class="otj-detail-full-width">
            <div class="otj-detail-item-full">
              <strong>Learning Description:</strong>
              <span>{{ otjData.learning_description }}</span>
            </div>
            <div v-if="otjData.competencies && otjData.competencies.length > 0" class="otj-detail-item-full">
              <strong>Competencies:</strong>
              <div class="competencies-tags">
                <span v-for="competency in otjData.competencies" :key="competency.id" class="badge competency-tag">
                  {{ competency.label }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <form>
        <label for="reject-comment">Please provide a reason for rejecting this OTJ</label>
        <fieldset class="form-group form-entry">
          <textarea id="reject-comment" placeholder="Add a comment" class="form-control" v-model="coachComment"></textarea>
        </fieldset>
      </form>
    </template>
    <template #footer>
      <button class="btn btn-outlined" @click="$emit('close')">Close</button>
      <button class="btn btn-success" @click="addFeedbackToOtjEntry(otjData?.id)" :disabled="!coachComment || !otjData">Confirm Rejection</button>
    </template>
  </k-modal>
</template>

<style scoped>
.fa-info-circle {
  position: relative;
  top: -5px;
  left: -2px;
  font-size: 0.9rem;
}

.otj-details-section h4 {
  margin-top: 0;
  margin-bottom: 12px;
  font-size: 1.2rem;
}

.otj-detail-container {
  background-color: var(--kate-panel-light);
  border: var(--kate-panel-light-border);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.otj-detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
}

.otj-detail-item {
  display: flex;
  flex-direction: row;
  gap: 6px;
  align-items: center;
}

.otj-detail-full-width {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.otj-detail-item-full {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.otj-detail-item strong,
.otj-detail-item-full strong {
  color: var(--kate-type-light);
  letter-spacing: 0.5px;
}

.otj-detail-item span,
.otj-detail-item-full span {
  color: var(--kate-type-light);
  font-size: 1rem;
  line-height: 1.4;
}

.competencies-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}
</style>

<script>
import TimeMixin from '@mixins/time-mixins';
import KModal from '@base-components/k-modal.vue';
import KTooltip from '@base-components/k-tooltip.vue';

export default {
  components: {
    KModal,
    KTooltip,
  },
  mixins: [TimeMixin],
  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
    otjData: {
      type: Object,
      required: true,
    },
    programmeId: {
      type: [String, Number],
      required: true,
    },
    programmeMemberId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      coachComment: '',
    };
  },
  methods: {
    formatTimeSpent(minutes) {
      const minutesValue = minutes ?? 0;
      const hours = Math.floor(minutesValue / 60);
      const remainingMinutes = minutesValue % 60;
      if (hours === 0) {
        return `${remainingMinutes}m`;
      }
      if (remainingMinutes === 0) {
        return `${hours}h`;
      }
      return `${hours}h ${remainingMinutes}m`;
    },

    addFeedbackToOtjEntry(otjId) {
      const jsonPayload = {
        comment: this.coachComment,
        programme_member_id: this.programmeMemberId,
      };
      this.$logger.info('Adding feedback to OTJ entry', { otjId }, true);
      this.$http.post(`/api/curriculum/programmes/${this.programmeId}/otj-entries/${otjId}/feedback`, jsonPayload)
        .then(() => {
          this.$logger.info('Feedback added to OTJ entry', { otjId });
          this.$emit('update-status', otjId, false);
          this.$emit('close');
          this.coachComment = '';
        })
        .catch(err => {
          this.$logger.error('Error adding feedback to OTJ entry', { otjId }, err);
          this.$ktoast.showError(err, true);
        });
    },
  },
};
</script>
